import { customTemplateDisplay } from "@/utils/templateNames";
import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IonSegment, IonSegmentButton, IonLabel, IonIcon, IonButton, IonList, IonItem, IonContent, IonSpinner, IonCheckbox } from "@ionic/react";
import {
  refreshOutline, documentTextOutline, eyeOutline, downloadOutline,
  chevronDownOutline, caretDownOutline, trashOutline, closeOutline, timeOutline,
} from "ionicons/icons";
import { IonDateInput } from "@/components/DateInput";
import { IonInput, IonSelect, IonSelectOption } from "@ionic/react";
import { confirmAlert } from "@/utils/confirmAlert";
import SwipeRow from "@/components/SwipeRow";
import { deleteWithUndo, withoutPendingDeletes } from "@/utils/undoDelete";
import { readListCache, writeListCache, patchListCache, userListScope } from "@/utils/listCache";
import { Eye, X } from "lucide-react";
import { toast } from "@/utils/toast";
import { toastText } from "@/utils/toastTemplates";
import { isNative } from "@/utils/nativeHttp";
import { shareBlob } from "@/utils/nativeSave";
import { getVisitorId } from "@/utils/trackPageView";
import { userHeaders, isUserLoggedIn } from "@/utils/userAuth";
import { useAppLive } from "@/utils/appLive";
import { deliverDownload } from "@/utils/pendingDownload";
import AppLayout from "@/components/AppLayout";
import { useSavedAssets } from "@/components/SavedAssets";
import FreshPopover from "@/components/FreshPopover";
import { NoResults } from "@/components/NotificationsDrawer";
import AppSeo from "@/components/AppSeo";
import AdminDetailModal from "@/components/AdminDetailModal";
import AdminListItem from "@/components/AdminListItem";
import DocumentPreviewModal from "@/components/DocumentPreviewModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "ms:docToken";

// ── Same document vocabulary and table treatment as the admin Saved Docs page ──
const DOCUMENT_TYPES = {
  "paystub":               "Pay Stub",
  "canadian-paystub":      "Canadian Pay Stub",
  "uk-payslip":            "UK Payslip",
  "resume":                "AI Resume",
  "w2":                    "W-2 Form",
  "w9":                    "W-9 Form",
  "1099-nec":              "1099-NEC",
  "1099-misc":             "1099-MISC",
  "bank-statement":        "Accounting Mockup",
  "offer-letter":          "Offer Letter",
  "cease-and-desist":      "Cease and Desist",
  "power-of-attorney":     "Power of Attorney",
  "commercial-lease":      "Commercial Lease",
  "vehicle-bill-of-sale":  "Vehicle Bill of Sale",
  "schedule-c":            "Schedule C",
  "utility-bill":          "Utility Bill",
  "legal-document":        "Legal Document",
  "custom-pdf":            "Signed Document",
};

const DOC_COLORS = {
  "paystub": "#34d399", "canadian-paystub": "#34d399", "uk-payslip": "#34d399", "resume": "#2563eb", "w2": "#7c3aed", "w9": "#7c3aed",
  "1099-nec": "#d97706", "1099-misc": "#d97706", "bank-statement": "#0891b2", "offer-letter": "#34d399",
  "cease-and-desist": "#b91c1c", "power-of-attorney": "#7c3aed", "commercial-lease": "#0891b2",
  "vehicle-bill-of-sale": "#dc2626", "schedule-c": "#92400e", "utility-bill": "#64748b",
  "legal-document": "#7c3aed", "custom-pdf": "#0d9488",
};

const tdBase = {
  padding: "0 12px",
  fontSize: "0.875rem",
  color: "var(--ion-text-color)",
  borderBottom: "1px solid var(--ion-border-color)",
  height: 64,
  verticalAlign: "middle",
};

const segBtnStyle = {
  "--color":           "var(--ion-color-medium)",
  "--color-checked":   "var(--ion-text-color)",
  "--indicator-color": "var(--ion-text-color)",
  "--border-radius":   "0",
  "--padding-top":     "0",
  "--padding-bottom":  "0",
  minHeight: 46,
  flexShrink: 0,
};

const CATEGORY_TYPES = {
  "paystub":          ["paystub"],
  "canadian-paystub": ["canadian-paystub"],
  "uk-payslip": ["uk-payslip"],
  "tax-forms":        ["w2", "w9", "1099-nec", "1099-misc", "schedule-c"],
  "legal-forms":      ["cease-and-desist", "power-of-attorney", "vehicle-bill-of-sale", "legal-document"],
  "business-forms":   ["bank-statement", "utility-bill", "commercial-lease", "offer-letter"],
  "resumes":          ["resume", "ai-resume"],
};

const TABS = [
  { value: "all",              label: "ALL" },
  { value: "paystub",          label: "PAY STUBS" },
  { value: "canadian-paystub", label: "CANADIAN STUBS" },
  { value: "uk-payslip", label: "UK PAYSLIPS" },
  { value: "tax-forms",        label: "TAX FORMS" },
  { value: "legal-forms",      label: "LEGAL FORMS" },
  { value: "business-forms",   label: "BUSINESS FORMS" },
  { value: "resumes",          label: "RESUMES" },
];

function expiryLabel(doc) {
  let exp = null;
  if (doc.expiresAt) exp = new Date(doc.expiresAt);
  if (!exp || isNaN(exp.getTime())) return null;
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(exp) - startOfDay(new Date())) / 86400000);
  if (days <= 0) return "Expires today";
  return `Expires in ${days}d`;
}

export default function AppMyDocuments() {
  const location = useLocation();
  const navigate = useNavigate();

  // This browser's documents load by its anonymous visitor id — no email
  // needed. An emailed link (token) adds documents bought on another device.
  const visitorId = getVisitorId();
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  });

  const [docs, setDocs]       = useState(() => readListCache("myDocs", userListScope())?.docs || []); // the last rows paint at once
  const [loading, setLoading] = useState(() => !readListCache("myDocs", userListScope())); // a spinner only with nothing to show yet
  const [segment, setSegment] = useState("all");
  const [detail, setDetail]   = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null); // the /app notification-style page preview
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // An emailed link (?token=) can still add documents bought under an email
  const [linkedEmail, setLinkedEmail] = useState("");

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // On the web this page is for signed-in accounts. A guest gets in only with
  // an emailed documents link (the token, new or kept from an earlier visit);
  // any other visit, or a link that has expired, goes to sign in and back.
  // The native app keeps it open to guests.
  useEffect(() => {
    if (isNative || isUserLoggedIn()) return;
    if (new URLSearchParams(location.search).get("token") || token) return;
    navigate(`/login?next=${encodeURIComponent(`/app/my-documents${location.search}`)}`, { replace: true });
  }, [token, location.search]); // eslint-disable-line

  // A link from the email arrives as ?token=… — keep it, drop it from the URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("token");
    if (t) {
      try { localStorage.setItem(TOKEN_KEY, t); } catch {}
      setToken(t);
      navigate("/app/my-documents", { replace: true });
    }
  }, [location.search]); // eslint-disable-line

  const accessQuery = useCallback(() => {
    const p = new URLSearchParams();
    if (visitorId) p.set("visitorId", visitorId);
    if (token) p.set("token", token);
    // Ids the archive handed back after past purchases on this browser
    try {
      const ids = JSON.parse(localStorage.getItem("ms:docIds") || "[]");
      if (Array.isArray(ids) && ids.length) p.set("ids", ids.slice(-100).join(","));
    } catch {}
    return p.toString();
  }, [visitorId, token]);

  const fetchDocs = useCallback(async (silent = false) => {
    if (!silent && !readListCache("myDocs", userListScope())) setLoading(true);
    try {
      // Signed in: the token adds the account's documents (and attaches this device's)
      const res = await fetch(`${BACKEND_URL}/api/my-documents?${accessQuery()}`, { headers: userHeaders() });
      if (res.status === 401 && token) {
        // Expired email link — drop it and fall back to this device's documents
        try { localStorage.removeItem(TOKEN_KEY); } catch {}
        setToken("");
        setLinkedEmail("");
        return;
      }
      const data = await res.json();
      if (data.success) {
        const list = withoutPendingDeletes(data.documents || []); // a swipe still deleting stays gone
        setDocs(list);
        setLinkedEmail(data.email || "");
        writeListCache("myDocs", { docs: list }, userListScope());
      }
    } catch (_) {
      toast.error("Couldn't load your documents. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [accessQuery, token]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);
  useAppLive(fetchDocs); // new documents show up as they are made, no reload

  // ?doc=<id> (from the topbar search) opens that document's preview
  useEffect(() => {
    const id = new URLSearchParams(location.search).get("doc");
    if (!id || loading) return;
    const found = docs.find((d) => d.id === id);
    if (found) setPreviewDoc(found);
    navigate("/app/my-documents", { replace: true });
  }, [docs, loading, location.search]); // eslint-disable-line

  const fileUrl = (doc) => `${BACKEND_URL}/api/my-documents/${doc.id}/download?${accessQuery()}`;

  const loadFile = async (doc) => {
    const res = await fetch(fileUrl(doc), { headers: userHeaders() });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${res.status}`); }
    return res.blob();
  };

  // View opens the same page preview as the notifications drawer
  const viewDoc = (doc) => setPreviewDoc(doc);

  const downloadDoc = async (doc) => {
    try {
      const res = await fetch(fileUrl(doc), { headers: userHeaders() });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${res.status}`); }
      deliverDownload(await res.blob(), doc.fileName || "document");
    } catch (err) {
      toast.error(`Download failed: ${err.message}`);
    }
  };

  // Native only: the system share sheet for this document
  const [shareBusy, setShareBusy] = useState(false);
  const shareDoc = async (doc) => {
    if (shareBusy) return;
    setShareBusy(true);
    try {
      const res = await fetch(fileUrl(doc), { headers: userHeaders() });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${res.status}`); }
      await shareBlob(await res.blob(), doc.fileName || "document.pdf");
    } catch (err) {
      toast.error(`Could not share: ${err.message}`);
    } finally {
      setShareBusy(false);
    }
  };

  // ── mass delete: the whodat payments selection pattern ──
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const setChecked = (id, on) => setSelected((p) => { const n = new Set(p); if (on) n.add(id); else n.delete(id); return n; });
  const setAllChecked = (idList, on) => setSelected((p) => { const n = new Set(p); idList.forEach((i) => (on ? n.add(i) : n.delete(i))); return n; });
  const clearSelected = () => setSelected(new Set());

  const deleteRequest = (idList) => ({
    url: `${BACKEND_URL}/api/my-documents/delete?${accessQuery()}`,
    method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ ids: idList }),
  });
  const deleteOnServer = async (idList) => {
    const { url, ...init } = deleteRequest(idList);
    const res = await fetch(url, init);
    const d = await res.json().catch(() => ({}));
    if (!res.ok || d.success === false) throw new Error(d.detail || "Could not delete");
    return d.deleted;
  };
  const deleteByIds = async (idList) => {
    const deleted = await deleteOnServer(idList);
    setDocs((prev) => prev.filter((x) => !idList.includes(x.id)));
    setSelected((p) => { const n = new Set(p); idList.forEach((i) => n.delete(i)); return n; });
    toast.success(`${deleted} document${deleted === 1 ? "" : "s"} deleted`);
  };

  const bulkDelete = async () => {
    const idList = [...selected];
    if (!idList.length) return;
    if (!(await confirmAlert({
      header: `Delete ${idList.length} document${idList.length === 1 ? "" : "s"}?`,
      message: "They disappear from My Documents for good. This cannot be undone.",
      confirmText: "Delete",
    }))) return;
    setBulkBusy(true);
    try { await deleteByIds(idList); } catch (err) { toast.error(err.message); } finally { setBulkBusy(false); }
  };

  // mobile: a full left-swipe deletes one row the way mail apps do: the row is
  // gone at once, a toast offers Undo for a few seconds, then the server delete
  const swipeDelete = (doc) => deleteWithUndo({
    noun: "document",
    ids: [doc.id],
    // the cached list follows, so a reload paints without the row (or with it back after Undo)
    remove: () => setDocs((prev) => { const next = prev.filter((x) => x.id !== doc.id); patchListCache("myDocs", "docs", next, userListScope()); return next; }),
    restore: () => setDocs((prev) => { const next = prev.some((x) => x.id === doc.id) ? prev : [...prev, doc].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)); patchListCache("myDocs", "docs", next, userListScope()); return next; }),
    request: deleteRequest([doc.id]), // recorded until the server answers, so a refresh still carries it out
    after: () => setSelected((p) => { const n = new Set(p); n.delete(doc.id); return n; }),
  });

  // ── scheduled delivery (Pro/Enterprise): mail a document later ──
  const navigate2 = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [scheduleFor, setScheduleFor] = useState(null);
  const [deliveriesOpen, setDeliveriesOpen] = useState(false); // the banner starts collapsed

  // pay stub template ids wear their style names ("template-b" was ADP, retired, renders as Gusto)
  const TEMPLATE_NAMES = { "template-a": "Gusto", "template-b": "Gusto", "template-c": "Workday", "template-h": "OnPay" };
  const templateLabel = (doc) => {
    const v = String((doc && doc.template) || "");
    if (TEMPLATE_NAMES[v]) return TEMPLATE_NAMES[v];
    const c = customTemplateDisplay(doc, { yours: true }); // a design's own name, resolved by the server
    return c ? c.label : v;
  };
  const [schedEmail, setSchedEmail] = useState("");
  const [schedDate, setSchedDate] = useState("");
  const [schedHour, setSchedHour] = useState("9");
  const [schedNote, setSchedNote] = useState("");
  const [schedBusy, setSchedBusy] = useState(false);

  const loadDeliveries = useCallback(async () => {
    if (!isUserLoggedIn()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/document-deliveries`, { headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success !== false) setDeliveries(d.deliveries || []);
    } catch { /* the list stays as it was */ }
  }, []);
  useEffect(() => { loadDeliveries(); }, [loadDeliveries]);

  // one pending delivery per document: scheduling again is blocked until it
  // sends or is canceled
  const hasPendingDelivery = (doc) => deliveries.some((dl) => dl.status === "pending" && dl.documentId === doc.id);

  const openSchedule = (doc) => {
    if (!bizAccess) {
      toast.error("Scheduled delivery comes with Pro and Enterprise");
      navigate2("/app/billing");
      return;
    }
    if (hasPendingDelivery(doc)) {
      toast.error("This document is already scheduled. Cancel that delivery first.");
      return;
    }
    setSchedEmail(""); setSchedDate(""); setSchedHour("9"); setSchedNote("");
    setScheduleFor(doc);
  };

  const scheduleSend = async () => {
    const email = schedEmail.trim();
    if (!email.includes("@")) { toast.error("Enter the email to send to"); return; }
    if (!schedDate) { toast.error("Pick the delivery date"); return; }
    const sendAt = new Date(`${schedDate}T${String(schedHour).padStart(2, "0")}:00:00`);
    if (isNaN(sendAt.getTime()) || sendAt < new Date()) { toast.error("Pick a time that is still ahead"); return; }
    if (scheduleFor.expiresAt && sendAt > new Date(scheduleFor.expiresAt)) {
      toast.error(`This document expires ${fmtDeliveryTime(scheduleFor.expiresAt)}. Pick an earlier time`);
      return;
    }
    setSchedBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/document-deliveries`, {
        method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ documentId: scheduleFor.id, email, sendAt: sendAt.toISOString(), note: schedNote.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not schedule");
      toast.success(toastText("delivery_scheduled", { file: scheduleFor.fileName || "Document", email }, "{file} is scheduled for {email}"));
      setScheduleFor(null);
      loadDeliveries();
    } catch (err) { toast.error(err.message); } finally { setSchedBusy(false); }
  };

  const cancelDelivery = async (d) => {
    if (!(await confirmAlert({ header: "Cancel this delivery?", message: `${d.fileName} will not be sent to ${d.email}.`, confirmText: "Cancel it" }))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/document-deliveries/${d.id}`, { method: "DELETE", headers: userHeaders() });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) throw new Error(j.detail || "Could not cancel");
      toast.success(toastText("delivery_canceled", {}, "Delivery canceled"));
      loadDeliveries();
    } catch (err) { toast.error(err.message); }
  };

  const fmtDeliveryTime = (iso) => {
    try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric" }); }
    catch { return iso; }
  };

  const bulkButtons = selected.size > 0 && (
    <>
      <IonButton size="small" fill="clear" className="docs-clear-btn" onClick={clearSelected} style={{ "--color": "var(--ion-text-color)" }}>
        <IonIcon slot="start" icon={closeOutline} /> Clear
      </IonButton>
      <IonButton size="small" fill="clear" className="docs-delete-btn" disabled={bulkBusy} onClick={bulkDelete} style={{ "--color": "#c5000f" }}>
        <IonIcon slot="start" icon={trashOutline} /> Delete
      </IonButton>
    </>
  );

  // The business's own categories (and the admin's) are segments of their
  // own. A document belongs to a category when the category page generated
  // it (its documentType is the slug) or when it was made from a design
  // filed under that category through the generator the design came from
  // (its template is the design).
  const [docCategories, setDocCategories] = useState([]);
  const [templateCategory, setTemplateCategory] = useState({}); // template id -> category slug
  useEffect(() => {
    let alive = true;
    const json = (r) => (r.ok ? r.json() : Promise.resolve({})).catch(() => ({}));
    const admin = fetch(`${BACKEND_URL}/api/doc-template-categories`).then(json).then((d) => d.categories || []).catch(() => []);
    const adminTpl = fetch(`${BACKEND_URL}/api/doc-templates`).then(json).then((d) => d.templates || []).catch(() => []);
    const own = isUserLoggedIn()
      ? fetch(`${BACKEND_URL}/api/user/doc-template-categories?live=1`, { headers: userHeaders() }).then(json).then((d) => (d.hasAccess ? d.categories || [] : [])).catch(() => [])
      : Promise.resolve([]);
    const ownTpl = isUserLoggedIn()
      ? fetch(`${BACKEND_URL}/api/user/doc-templates`, { headers: userHeaders() }).then(json).then((d) => (d.hasAccess ? d.templates || [] : [])).catch(() => [])
      : Promise.resolve([]);
    Promise.all([admin, own, adminTpl, ownTpl]).then(([a, o, at, ot]) => {
      if (!alive) return;
      setDocCategories([...a, ...o.filter((c) => !a.some((x) => x.slug === c.slug))]);
      const map = {};
      for (const t of [...at, ...ot]) if (t && t.id) map[t.id] = t.documentType;
      setTemplateCategory(map);
    });
    return () => { alive = false; };
  }, [docs.length]); // eslint-disable-line
  // the count beside a category, one size everywhere: the desktop segment,
  // the phone dropdown's label and its popover rows
  const countBadge = { display: "inline-flex", alignItems: "center", justifyContent: "center", height: 24, minWidth: 17, padding: "0 5px", boxSizing: "border-box", background: "var(--ion-background-color)", borderRadius: 4, fontSize: "0.65rem", fontWeight: 700, lineHeight: 1, letterSpacing: 0, color: "var(--ion-color-medium)" };
  // label: the segment's uppercase; title: the name as the phone dropdown lists it
  const titleCase = (s) => String(s).toLowerCase().replace(/(^|\s)\S/g, (m) => m.toUpperCase());
  const tabs = [
    ...TABS.map((t) => ({ ...t, title: titleCase(t.label) })),
    ...docCategories.map((c) => ({ value: c.slug, label: String(c.label || c.slug).toUpperCase(), title: String(c.label || c.slug) })),
  ];
  // a document from a category page is labelled with the category's name
  const docTypeLabel = (t) => DOCUMENT_TYPES[t] || (docCategories.find((c) => c.slug === t) || {}).label || t || "-";
  const designId = (t) => { const m = /^(?:usercustom|custom):(.+)$/.exec(String(t || "")); return m ? m[1] : null; };
  const inSegment = (d, seg) => {
    if (seg === "all") return true;
    const types = CATEGORY_TYPES[seg] || [seg];
    if (types.includes(d.documentType)) return true;
    const id = designId(d.template);
    return !!id && templateCategory[id] === seg;
  };
  const filtered = docs.filter((d) => inSegment(d, segment));
  const counts = Object.fromEntries([
    ["all", docs.length],
    ...tabs.filter((t) => t.value !== "all").map((t) => [t.value, docs.filter((d) => inSegment(d, t.value)).length]),
  ]);

  const emptyState = <NoResults text="No documents yet" />;
  // A connected business puts its name on the page: "A1 Lawn Care Documents"
  const { hasAccess: bizAccess, business } = useSavedAssets();
  const docsTitle = bizAccess && business?.name ? `${business.name} Documents` : "My Documents";
  // Mobile: the categories collapse into the top bar's dropdown pattern —
  // the current one as a button, a fresh-mounted popover listing the rest.
  const [catMenu, setCatMenu] = useState({ open: false, event: undefined });
  const activeTabDef = tabs.find((t) => t.value === segment) || tabs[0];

  return (
    <AppLayout fillHeight>
      <AppSeo title="My Documents" description="Find and re-download the documents you've created with MintSlip." path="/app/my-documents" />
      <div style={{ padding: 10, height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "4px 6px" }}>
          <div style={{ display: "flex", flexDirection: "column", flex: "1 1 0%", overflow: "hidden", background: "var(--ion-card-background)", borderRadius: 6, boxShadow: "0 4px 24px rgba(0,0,0,0.18)" }}>

            {/* ── Card header ── */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 24px", flexShrink: 0, gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: "0 0 2px", fontWeight: 700, fontSize: "1.1rem", color: "var(--ion-text-color)", letterSpacing: "-0.01em" }}>{docsTitle}</h2>
                <p style={{ margin: 0, fontSize: "0.875rem", color: "var(--ion-color-medium)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {docs.length} saved document{docs.length === 1 ? "" : "s"}{isUserLoggedIn() ? "" : linkedEmail ? ` · includes ${linkedEmail}` : " · this device"}
                </p>
              </div>
            </div>

            {/* ── Segment row ── */}
            <div style={{ display: "flex", alignItems: "stretch", background: "var(--ion-card-background)", borderBottom: "1px solid var(--ion-border-color)", flexShrink: 0 }}>
              {isMobile ? (
                <>
                  {/* the top bar's segment label (uppercase, a caret at its end)
                      in the card's own text colour, its count beside it, opening
                      the lined popover of categories */}
                  <IonButton fill="clear" className="segment-button mydocs-cat-btn" onClick={(e) => setCatMenu({ open: true, event: e.nativeEvent })}
                    style={{ "--color": "var(--ion-text-color)", margin: "4px 0 4px 8px" }}>
                    {activeTabDef.label}
                    <span style={{ ...countBadge, marginLeft: 8 }}>
                      {counts[activeTabDef.value]}
                    </span>
                    <IonIcon slot="end" icon={caretDownOutline} />
                  </IonButton>
                  <FreshPopover open={catMenu.open} event={catMenu.event} onClose={() => setCatMenu({ open: false, event: undefined })} side="bottom" alignment="start" style={{ "--width": "240px" }}>
                    <IonContent>
                      <IonList style={{ padding: "4px 0" }}>
                        {tabs.map((tab, i) => (
                          <IonItem
                            key={tab.value}
                            button
                            detail={false}
                            lines={i === tabs.length - 1 ? "none" : "inset"}
                            aria-current={segment === tab.value ? "page" : undefined}
                            onClick={() => { setCatMenu({ open: false, event: undefined }); setSegment(tab.value); }}
                            style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--color": "var(--ion-text-color)", "--border-color": "var(--app-divider)" }}
                          >
                            <IonLabel>{tab.title || tab.label}</IonLabel>
                            <span slot="end" style={countBadge}>
                              {counts[tab.value]}
                            </span>
                          </IonItem>
                        ))}
                      </IonList>
                    </IonContent>
                  </FreshPopover>
                  <div style={{ flex: "1 1 0%" }} />
                </>
              ) : (
              <IonSegment scrollable value={segment} onIonChange={e => setSegment(e.detail.value)} style={{ "--background": "transparent", flex: "1 1 0%" }}>
                {tabs.map(tab => (
                  <IonSegmentButton key={tab.value} value={tab.value} layout="label-only" style={segBtnStyle}>
                    <IonLabel style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
                      {tab.label}
                      <span style={countBadge}>
                        {counts[tab.value]}
                      </span>
                    </IonLabel>
                  </IonSegmentButton>
                ))}
              </IonSegment>
              )}
              <div style={{ display: "flex", alignItems: "center", paddingRight: 12, flexShrink: 0 }}>
                <IonButton title="Refresh" fill="clear" shape="round" color="medium" onClick={fetchDocs}>
                  <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1rem" }}>
                    <IonIcon icon={refreshOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                  </span>
                </IonButton>
              </div>
            </div>

            {deliveries.some((d) => d.status === "pending") && (
              <div className="docs-deliveries" style={{ flexShrink: 0, padding: "10px 16px", background: "var(--ion-color-tertiary, #5260ff)", color: "#ffffff", display: "flex", flexDirection: "column" }}>
                <div role="button" tabIndex={0} onClick={() => setDeliveriesOpen((v) => !v)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setDeliveriesOpen((v) => !v); }}
                  style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
                  <IonIcon icon={timeOutline} style={{ color: "#ffffff", fontSize: 18, flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, fontSize: "0.9rem", flex: 1 }}>
                    Scheduled Email Deliveries
                    <span style={{ marginLeft: 8, background: "var(--ion-background-color)", borderRadius: 4, padding: "1px 5px", fontSize: "0.65rem", fontWeight: 700, color: "var(--ion-color-medium)" }}>
                      {deliveries.filter((d) => d.status === "pending").length}
                    </span>
                  </span>
                  <IonIcon icon={chevronDownOutline} style={{ color: "#ffffff", fontSize: 18, flexShrink: 0, transition: "transform 0.2s", transform: deliveriesOpen ? "rotate(180deg)" : "none" }} />
                </div>
                <div className={"docs-deliveries-body" + (deliveriesOpen ? " open" : "")} aria-hidden={!deliveriesOpen}>
                  <div className="docs-deliveries-body-inner">
                    <p className="docs-deliveries-desc" style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.85)" }}>
                      These documents send themselves by email at the scheduled time. Cancel one to keep it here instead.
                    </p>
                    {deliveries.filter((d) => d.status === "pending").map((d) => (
                      <div key={d.id} className="docs-delivery-row">
                        <span className="docs-delivery-text">
                          <strong>{d.fileName}</strong><span className="docs-delivery-meta"> to {d.email} · {fmtDeliveryTime(d.sendAt)}</span>
                        </span>
                        <IonButton size="small" fill="clear" style={{ "--color": "#ffd7d7", margin: 0, fontWeight: 700, flexShrink: 0 }} onClick={() => cancelDelivery(d)}>Cancel</IonButton>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Table ── */}
            <div style={{ flex: "1 1 0%", overflow: "auto" }}>
              {loading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                  <IonSpinner name="crescent" />
                </div>
              ) : isMobile ? (
                filtered.length === 0 ? emptyState : (
                  <IonList lines="full" style={{ background: "transparent", padding: 0 }}>
                    {filtered.map(doc => {
                      const label = docTypeLabel(doc.documentType);
                      const size = doc.fileSize ? `${(doc.fileSize / 1024).toFixed(1)} KB` : null;
                      const date = doc.createdAt ? new Date(doc.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
                      return (
                        <SwipeRow key={doc.id} onDelete={() => swipeDelete(doc)}>
                          <AdminListItem
                          onClick={() => setDetail(doc)}
                          title={label}
                          badges={doc.fileExists === false && <span className="admin-badge admin-badge-red">Missing</span>}
                          subtitle={doc.fileName || "-"}
                          meta={[size, date, expiryLabel(doc)].filter(Boolean).join(" · ")}
                        />
                        </SwipeRow>
                      );
                    })}
                  </IonList>
                )
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                    <thead>
                      <tr>
                        <th style={{ padding: "10px 12px", width: 44, minWidth: 44, textAlign: "left", background: "var(--ion-background-color)" }}>
                          <IonCheckbox checked={filtered.length > 0 && filtered.every((d) => selected.has(d.id))}
                            onIonChange={(e) => setAllChecked(filtered.map((d) => d.id), e.detail.checked)}
                            aria-label="Select all" style={{ "--size": "18px", verticalAlign: "middle" }} />
                        </th>
                        {[["Document", 200], ["File", 260], ["Size", 80], ["Created", 110]].map(([h, w]) => (
                          <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: "0.72rem", fontWeight: 400, color: "var(--ion-color-medium)", background: "var(--ion-background-color)", whiteSpace: "nowrap", ...(w ? { width: w, minWidth: w } : {}) }}>
                            {h}
                          </th>
                        ))}
                        <th style={{ padding: "10px 12px", width: 128, minWidth: 128, background: "var(--ion-background-color)", position: "relative" }}>
                          {selected.size > 0 && (
                            <div style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                              {bulkButtons}
                            </div>
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 && (
                        <tr><td colSpan={6} style={{ padding: 0 }}>{emptyState}</td></tr>
                      )}
                      {filtered.map(doc => {
                        const label = docTypeLabel(doc.documentType);
                        return (
                          <tr key={doc.id} style={{ position: "relative", height: 64, transform: "translateZ(0)" }}>
                            {/* Select — raised above the row overlay */}
                            <td style={{ ...tdBase, width: 44, minWidth: 44, position: "relative", zIndex: 2 }} onClick={(e) => e.stopPropagation()}>
                              <IonCheckbox checked={selected.has(doc.id)} onIonChange={(e) => setChecked(doc.id, e.detail.checked)}
                                aria-label="Select document" style={{ "--size": "18px", verticalAlign: "middle" }} />
                            </td>
                            {/* Document — hosts the row-wide click/ripple overlay */}
                            <td style={{ ...tdBase, minWidth: 200 }}>
                              <div
                                className="ion-activatable"
                                onClick={() => setDetail(doc)}
                                style={{ position: "absolute", left: 0, top: 0, width: "100%", height: "100%", overflow: "hidden", cursor: "pointer", zIndex: 1 }}
                              >
                                <ion-ripple-effect />
                              </div>
                              <span style={{ fontSize: "0.78rem", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>{label}</span>
                            </td>

                            {/* File — raised above the row overlay so the view link opens the PDF */}
                            <td style={{ ...tdBase, minWidth: 260, position: "relative", zIndex: 2 }}>
                              {doc.fileExists === false ? (
                                <span style={{ color: "var(--ion-color-danger)", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 4 }}>
                                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>{doc.fileName}</span>
                                  <span style={{ fontSize: "0.72rem", color: "var(--ion-color-danger-tint)", flexShrink: 0 }}>(missing)</span>
                                </span>
                              ) : (
                                <button
                                  onClick={() => viewDoc(doc)}
                                  style={{ color: "#3b82f6", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 4, cursor: "pointer", background: "none", border: "none", padding: 0, maxWidth: "100%", overflow: "hidden" }}
                                >
                                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.fileName}</span>
                                </button>
                              )}
                            </td>

                            {/* Size */}
                            <td style={{ ...tdBase, minWidth: 80 }}>
                              <span style={{ fontSize: "0.75rem", color: "var(--ion-color-medium)" }}>
                                {doc.fileSize ? `${(doc.fileSize / 1024).toFixed(1)} KB` : "-"}
                              </span>
                            </td>

                            {/* Created + expiry */}
                            <td style={{ ...tdBase, minWidth: 110 }}>
                              <span style={{ fontSize: "0.75rem", whiteSpace: "nowrap", display: "block" }}>
                                {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "-"}
                              </span>
                              {expiryLabel(doc) && (
                                <span style={{ fontSize: "0.68rem", whiteSpace: "nowrap", display: "block", marginTop: 2, color: /today|in [1-7]d/.test(expiryLabel(doc)) ? "var(--ion-color-warning)" : "var(--ion-color-medium)" }}>
                                  {expiryLabel(doc)}
                                </span>
                              )}
                            </td>

                            {/* View + Download — raised above the row overlay */}
                            <td style={{ ...tdBase, padding: "0 8px", width: 128, position: "relative", zIndex: 2 }}>
                              <div style={{ display: "flex", gap: 4 }}>
                                {bizAccess && (
                                <button
                                  className="ion-activatable admin-action-btn docs-schedule-btn"
                                  style={{ width: 38, height: 38 }}
                                  onClick={() => openSchedule(doc)}
                                  disabled={doc.fileExists === false || hasPendingDelivery(doc)}
                                  title={hasPendingDelivery(doc) ? "Already scheduled - cancel the delivery above to reschedule" : "Schedule email"}
                                >
                                  <IonIcon icon={timeOutline} style={{ fontSize: 22 }} /><ion-ripple-effect />
                                </button>
                                )}
                                <button
                                  className="ion-activatable admin-action-btn"
                                  style={{ width: 38, height: 38 }}
                                  onClick={() => viewDoc(doc)}
                                  disabled={doc.fileExists === false}
                                  title="View"
                                >
                                  <IonIcon icon={eyeOutline} style={{ fontSize: 22 }} /><ion-ripple-effect />
                                </button>
                                <button
                                  className="ion-activatable admin-action-btn"
                                  style={{ width: 38, height: 38 }}
                                  onClick={() => downloadDoc(doc)}
                                  disabled={doc.fileExists === false}
                                  title="Download"
                                >
                                  <IonIcon icon={downloadOutline} style={{ fontSize: 22 }} /><ion-ripple-effect />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

            </div>

          </div>
        </div>
      </div>

      <AdminDetailModal
        isOpen={!!scheduleFor}
        onClose={() => setScheduleFor(null)}
        title="Schedule Email Delivery"
        rows={scheduleFor ? [["Document", scheduleFor.fileName || "-"]] : []}
      >
        {scheduleFor && (
          <div className="docs-schedule-form" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <IonInput fill="outline" labelPlacement="floating" label="Send to email" type="email" inputmode="email"
              value={schedEmail} onIonInput={(e) => setSchedEmail(e.detail.value ?? "")} />
            <IonDateInput label="Delivery date" value={schedDate} onChange={(v) => setSchedDate(v ?? "")} />
            <IonSelect fill="outline" labelPlacement="floating" label="Around" interface="popover" value={schedHour}
              onIonChange={(e) => setSchedHour(e.detail.value ?? "9")}>
              {Array.from({ length: 24 }, (_, h) => (
                <IonSelectOption key={h} value={String(h)}>
                  {h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`}
                </IonSelectOption>
              ))}
            </IonSelect>
            <IonInput fill="outline" labelPlacement="floating" label="Note (optional)"
              value={schedNote} onIonInput={(e) => setSchedNote(e.detail.value ?? "")} />
            <p style={{ margin: 0, fontSize: "0.75rem", color: "var(--ion-color-medium)" }}>
              The document is emailed as an attachment within the hour you pick.
              {scheduleFor.expiresAt ? ` This file expires ${fmtDeliveryTime(scheduleFor.expiresAt)} and cannot be sent after that.` : ""}
            </p>
            <IonButton expand="block" color="success" disabled={schedBusy} onClick={scheduleSend} style={{ "--color": "#ffffff" }}>
              {schedBusy ? <IonSpinner name="crescent" /> : "Schedule Delivery"}
            </IonButton>
          </div>
        )}
      </AdminDetailModal>

      {/* ── Page preview (the notifications-drawer preview, for any saved file) ── */}
      <DocumentPreviewModal doc={previewDoc} loadFile={loadFile} onClose={() => setPreviewDoc(null)} />

      {/* ── Detail modal (same treatment as admin, without the User row) ── */}
      <AdminDetailModal
        isOpen={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? (detail.fileName || "Document") : "Document"}
        rows={detail ? [
          ["Document", (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: DOC_COLORS[detail.documentType] || "#64748b", display: "inline-block" }} />
              {docTypeLabel(detail.documentType)}
            </span>
          )],
          detail.template && ["Template", templateLabel(detail)],
          ["File", (
            <span style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
              {detail.fileName || "-"}
              {detail.fileExists === false && <span className="admin-badge admin-badge-red" style={{ marginLeft: 8 }}>Missing</span>}
            </span>
          )],
          ["Size", detail.fileSize ? `${(detail.fileSize / 1024).toFixed(1)} KB` : "-"],
          ["Saved", detail.createdAt ? new Date(detail.createdAt).toLocaleString() : "-"],
          ["Expires", expiryLabel(detail) || "Never"],
        ] : []}
      >
        {detail && (
          <>
            <IonButton expand="block" color="primary" onClick={() => viewDoc(detail)} disabled={detail.fileExists === false}>
              {detail.fileExists === false ? "File Missing" : "View Document"}
            </IonButton>
            {detail.fileExists !== false && (
              <IonButton expand="block" color="success" style={{ "--color": "#ffffff" }} onClick={() => downloadDoc(detail)}>
                Download
              </IonButton>
            )}
            {/* The phones get the system share sheet (AirDrop, Drive, mail...) */}
            {isNative && detail.fileExists !== false && (
              <IonButton expand="block" fill="outline" disabled={shareBusy}
                style={{ "--color": "var(--ion-text-color, #0d1b2a)", "--border-color": "var(--ion-text-color, #0d1b2a)" }}
                onClick={() => shareDoc(detail)}>
                {shareBusy ? "Preparing…" : "Share This Document"}
              </IonButton>
            )}
            {/* Scheduled email delivery is a Pro and Enterprise feature */}
            {bizAccess && detail.fileExists !== false && (
              <IonButton expand="block" fill="outline" className="docs-schedule-btn" disabled={hasPendingDelivery(detail)}
                style={{ "--color": "var(--ion-text-color, #0d1b2a)", "--border-color": "var(--ion-text-color, #0d1b2a)" }}
                title={hasPendingDelivery(detail) ? "Already scheduled - cancel the delivery first" : undefined}
                onClick={() => { const doc = detail; setDetail(null); openSchedule(doc); }}>
                {hasPendingDelivery(detail) ? "Already Scheduled" : "Schedule Email"}
              </IonButton>
            )}
          </>
        )}
      </AdminDetailModal>
    </AppLayout>
  );
}
